import React, { useRef } from 'react';
import { Box, VStack, Text, Container, HStack, Heading, IconButton, Image } from '@chakra-ui/react';
import { ArrowBackIcon, ChevronUpIcon, ChevronDownIcon } from '@chakra-ui/icons';
import { CopyrightText } from './CopyrightText';
import { useLanguage } from '../i18n/LanguageContext';

interface FAQPageProps {
  onBackToHome?: () => void;
  currentTab?: 'dashboard' | 'faq';
}

const FAQPage: React.FC<FAQPageProps> = ({ onBackToHome, currentTab = 'faq' }) => {
  const { t } = useLanguage();
  const topRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const scrollToTop = () => {
    topRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToBottom = () => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Container
      maxW="container.md"
      flex="1"
      pt={{
        base: 'calc(env(safe-area-inset-top, 0px) + 16px)',
        md: 8,
      }}
      pb={{ base: 4, md: 8 }}
      px={{ base: 4, md: 6 }}
      data-testid="faq-page"
      position="relative"
    >
      <VStack spacing={6} align="stretch">
        <VStack spacing={4} ref={topRef}>
          <HStack justify="space-between" w="full">
            <IconButton
              onClick={onBackToHome}
              aria-label="Back to home"
              icon={<ArrowBackIcon boxSize={8} />}
              variant="ghost"
              size="xl"
            />
            <Heading as="h1" size="lg" color="white" textShadow="0 2px 12px rgba(0,0,0,0.9), 0 1px 4px rgba(0,0,0,0.8)">{t('faq')}</Heading>
            <Box w="48px" /> {/* Spacer for alignment */}
          </HStack>
        </VStack>
        
        <Box
          bg="rgba(4,7,18,0.78)"
          backdropFilter="blur(24px) saturate(160%)"
          border="1px solid rgba(255,255,255,0.11)"
          borderRadius="22px"
          p={5}
          sx={{ WebkitBackdropFilter: 'blur(24px) saturate(160%)' }}
        >
        <VStack align="start" spacing={8}>
          <Box>
            <Text fontWeight="700" color="white" mb={2} fontSize="15px">
              1. Why was this app developed?
            </Text>
            <Text fontStyle="italic" color="rgba(255,255,255,0.72)" fontSize="14px" lineHeight="1.6">
              This app was created to solve the common problem of forgetting where you parked your car in large parking lots or multi-level garages. Whether you're at a shopping mall, airport, or hospital, this simple tool helps you quickly save and remember your parking location with visual cues, photos, and notes. It's designed to be fast, offline-capable, and accessible to everyone without requiring accounts or subscriptions.
            </Text>
          </Box>
          
          <Box>
            <Text fontWeight="700" color="white" mb={2} fontSize="15px">
              2. Is this app free to use?
            </Text>
            <Text fontStyle="italic" color="rgba(255,255,255,0.72)" fontSize="14px" lineHeight="1.6">
              Yes! This web application is completely free to use!
            </Text>
          </Box>

          <Box>
            <Text fontWeight="700" color="white" mb={2} fontSize="15px">
              3. How do I use this app?
            </Text>
            <VStack align="start" spacing={2}>
              <Text fontStyle="italic" color="rgba(255,255,255,0.72)" fontSize="14px" lineHeight="1.6">
                Add your lot number, write optional notes like "near the red pillar", optionally select your parking level (L1-L5 or B1-B3), and either take a photo or hit "Save Location". That's it!
              </Text>
              <Text fontStyle="italic" color="rgba(255,255,255,0.72)" fontSize="14px" lineHeight="1.6">
                <Text as="span" fontWeight="600" color="white">Clear Inputs:</Text> If you want to start over, click the "🧹 Clear" button next to your profile name to reset all fields (parking level, lot number, notes, and photo) without saving.
              </Text>
            </VStack>
          </Box>

          <Box>
            <Text fontWeight="700" color="white" mb={2} fontSize="15px">
              4. How do User Profiles work?
            </Text>
            <VStack align="start" spacing={2}>
              <Text fontStyle="italic" color="rgba(255,255,255,0.72)" fontSize="14px" lineHeight="1.6">
                User Profiles allow you to manage parking spots for multiple vehicles or family members. Each profile maintains its own:
              </Text>
              <Text pl={4} fontStyle="italic" color="rgba(255,255,255,0.72)" fontSize="14px">• Current parking location</Text>
              <Text pl={4} fontStyle="italic" color="rgba(255,255,255,0.72)" fontSize="14px">• Parking history and records</Text>
              <Text pl={4} fontStyle="italic" color="rgba(255,255,255,0.72)" fontSize="14px">• Photos and notes</Text>
              <Text fontStyle="italic" color="rgba(255,255,255,0.72)" fontSize="14px" lineHeight="1.6">
                To access Profile Settings, click the circular profile icon in the top-right corner of the main screen.
              </Text>
            </VStack>
          </Box>

          <Box>
            <Text fontWeight="700" color="white" mb={2} fontSize="15px">
              5. How do I switch between profiles?
            </Text>
            <VStack align="start" spacing={2}>
              <Text fontStyle="italic" color="rgba(255,255,255,0.72)" fontSize="14px" lineHeight="1.6">
                Click the profile icon (showing initials) in the top-right corner to open Profile Settings. From there, you can:
              </Text>
              <Text pl={4} fontStyle="italic" color="rgba(255,255,255,0.72)" fontSize="14px">• Select a different active profile from the dropdown</Text>
              <Text pl={4} fontStyle="italic" color="rgba(255,255,255,0.72)" fontSize="14px">• View which profile is currently active</Text>
              <Text pl={4} fontStyle="italic" color="rgba(255,255,255,0.72)" fontSize="14px">• Edit profile names for better organization</Text>
              <Text fontStyle="italic" color="rgba(255,255,255,0.72)" fontSize="14px" lineHeight="1.6">
                You can rename profiles to anything meaningful like "Mom's Car", "Dad's Honda", "Work Vehicle", etc.
              </Text>
            </VStack>
          </Box>

          <Box>
            <Text fontWeight="700" color="white" mb={2} fontSize="15px">
              6. How do I customize profile names?
            </Text>
            <VStack align="start" spacing={2}>
              <Text fontStyle="italic" color="rgba(255,255,255,0.72)" fontSize="14px" lineHeight="1.6">
                In the Profile Settings modal (accessed via the profile icon):
              </Text>
              <Text pl={4} fontStyle="italic" color="rgba(255,255,255,0.72)" fontSize="14px">1. Click the "✏️ Edit" button next to any profile name</Text>
              <Text pl={4} fontStyle="italic" color="rgba(255,255,255,0.72)" fontSize="14px">2. Type your new custom name</Text>
              <Text pl={4} fontStyle="italic" color="rgba(255,255,255,0.72)" fontSize="14px">3. Press Enter or click the checkmark (✓) to save</Text>
              <Text pl={4} fontStyle="italic" color="rgba(255,255,255,0.72)" fontSize="14px">4. Click the X to cancel without saving</Text>
              <Text fontStyle="italic" color="rgba(255,255,255,0.72)" fontSize="14px" lineHeight="1.6">
                Your custom names are saved on your device. When you rename a profile, all its parking data and history automatically transfer to the new name.
              </Text>
            </VStack>
          </Box>

          <Box>
            <Text fontWeight="700" color="white" mb={2} fontSize="15px">
              7. Can I save more than one parking spot?
            </Text>
            <Text fontStyle="italic" color="rgba(255,255,255,0.72)" fontSize="14px" lineHeight="1.6">
              Yes! Each profile can save one current parking location, but every save creates a record in your Parking Records history. You can view, delete individual entries, or clear all records for the active profile.
            </Text>
          </Box>

          <Box>
            <Text fontWeight="700" color="white" mb={2} fontSize="15px">
              8. How do I take and view photos?
            </Text>
            <VStack align="start" spacing={2}>
              <Text fontStyle="italic" color="rgba(255,255,255,0.72)" fontSize="14px" lineHeight="1.6">
                <Text as="span" fontWeight="600" color="white">Taking Photos:</Text> Tap the camera icon, grant permission if prompted, and capture a photo. The app will show a preview before saving.
              </Text>
              <Text fontStyle="italic" color="rgba(255,255,255,0.72)" fontSize="14px" lineHeight="1.6">
                <Text as="span" fontWeight="600" color="white">Viewing Saved Photos:</Text> Click on any photo in your Current Location or Recent Records to view it full-screen.
              </Text>
            </VStack>
          </Box>

          <Box>
            <Text fontWeight="700" color="white" mb={2} fontSize="15px">
              9. What are the notification messages I see?
            </Text>
            <VStack align="start" spacing={2}>
              <Text fontStyle="italic" color="rgba(255,255,255,0.72)" fontSize="14px" lineHeight="1.6">
                The app uses toast notifications at the top of your screen:
              </Text>
              <Text pl={4} fontStyle="italic" color="rgba(255,255,255,0.72)" fontSize="14px">• <Text as="span" fontWeight="600" color="green.300">✅ Location Saved!</Text> — parking spot was saved</Text>
              <Text pl={4} fontStyle="italic" color="rgba(255,255,255,0.72)" fontSize="14px">• <Text as="span" fontWeight="600" color="red.300">❌ Camera not available</Text> — camera permission issues</Text>
            </VStack>
          </Box>

          <Box>
            <Text fontWeight="700" color="white" mb={2} fontSize="15px">
              10. Can I add this to my phone's home screen?
            </Text>
            <VStack align="start" spacing={3}>
              <Text fontStyle="italic" color="rgba(255,255,255,0.72)" fontSize="14px" lineHeight="1.6">
                Yes! This is a Progressive Web App (PWA). On mobile devices, tap the "Add to Home Screen" button or use your browser's menu. The app will install with a custom icon and work like a native app, even offline.
              </Text>
              <Box textAlign="center" w="100%">
                <Image
                  src={`${process.env.PUBLIC_URL}/mobile_app_icon.jpeg`}
                  alt="Mobile app icon showing car parking interface"
                  maxW="200px"
                  borderRadius="lg"
                  boxShadow="md"
                  mx="auto"
                />
                <Text fontSize="12px" color="rgba(255,255,255,0.5)" mt={2}>
                  Example of app icon on mobile home screen
                </Text>
              </Box>
            </VStack>
          </Box>

          <Box>
            <Text fontWeight="700" color="white" mb={2} fontSize="15px">
              11. Does my data sync across devices?
            </Text>
            <VStack align="start" spacing={2}>
              <Text fontStyle="italic" color="rgba(255,255,255,0.72)" fontSize="14px" lineHeight="1.6">
                No, all data is stored locally on each device for privacy and offline functionality. Your data never leaves your device and is not uploaded to any cloud servers.
              </Text>
              <Text fontSize="12px" color="rgba(255,255,255,0.45)" fontStyle="italic">
                (PS: I don't wish to incur extra cost for cloud storage on my server end as well, it's an app for everyone to use :P )
              </Text>
            </VStack>
          </Box>
        </VStack>
        </Box>
        
        <Box ref={bottomRef} />

        <CopyrightText />
      </VStack>

      {/* Floating Navigation Buttons */}
      <Box
        position="fixed"
        bottom={{ base: "120px", md: "100px" }}
        right={{ base: "20px", md: "30px" }}
        zIndex={1000}
      >
        <VStack spacing={2}>
          <IconButton
            onClick={scrollToTop}
            aria-label="Scroll to top"
            icon={<ChevronUpIcon boxSize={6} />}
            size="lg"
            isRound
            bg="transparent"
            border="1px solid"
            borderColor="accent.primary"
            color="accent.primary"
            _hover={{
              bg: "accent.primary",
              color: "white",
              transform: "scale(1.1)"
            }}
            _active={{
              transform: "scale(0.95)"
            }}
            transition="all 0.2s"
            boxShadow="lg"
            backdropFilter="blur(10px)"
          />
          <IconButton
            onClick={scrollToBottom}
            aria-label="Scroll to bottom"
            icon={<ChevronDownIcon boxSize={6} />}
            size="lg"
            isRound
            bg="transparent"
            border="1px solid"
            borderColor="accent.primary"
            color="accent.primary"
            _hover={{
              bg: "accent.primary",
              color: "white",
              transform: "scale(1.1)"
            }}
            _active={{
              transform: "scale(0.95)"
            }}
            transition="all 0.2s"
            boxShadow="lg"
            backdropFilter="blur(10px)"
          />
        </VStack>
      </Box>
    </Container>
  );
};

export default FAQPage; 